import React, { ReactNode, useContext, useMemo } from 'react';
import { IAvailableColumn } from '../view/ColumnSelector';

export type IViewContext = {
  viewScreenName: string;
  fullScreenName: string;
  nested?: boolean;
  views?: { [key: string]: any };
  currentView?: {
    //Partial/Work-In-Progress Type
    columns: Array<{ fieldName?: string; title?: string; total?: boolean }>;
    hideConfiguration?: boolean;
    name?: string;
    [filterType: string]: any;
  };
  availableColumns?: Array<IAvailableColumn>;
};

//Use to pass 'view' information down from the workspace.
//Would eventually like to move the current view management so having everything work via this will help.
const ViewContext = React.createContext({} as IViewContext);

/**
 * Wrapper around 'ViewContext.Provider' to build a full path to any given component.
 * Used to determine a unique path to TabPanel components for storing tab re-organization changes.
 */
export const ViewContextProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: Omit<IViewContext, 'fullScreenName'> | { uniqueName: string };
}) => {
  const { fullScreenName } = useContext(ViewContext);
  const viewContextValue = useMemo<IViewContext>(() => {
    const { uniqueName, ...contextValue } = value as IViewContext & { uniqueName: string };

    const newSegment = uniqueName ?? (value as IViewContext)?.viewScreenName;
    const newFullScreenName = fullScreenName ? `${fullScreenName}/${newSegment}` : newSegment;

    return {
      ...contextValue,
      fullScreenName: newFullScreenName,
      nested: !!fullScreenName,
    };
  }, [fullScreenName, value]);

  return <ViewContext.Provider value={viewContextValue}>{children}</ViewContext.Provider>;
};

export default ViewContext;
